import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { BookLink, PHONE_TEL, PHONE_DISPLAY } from "./booking";

const LINKS = [
  { label: "Home", href: "#home" },
  { label: "Services", href: "#services" },
  { label: "About", href: "#about" },
  { label: "Gallery", href: "#gallery" },
  { label: "Specials", href: "#specials" },
  { label: "FAQ", href: "#faq" },
  { label: "Contact", href: "#contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
          scrolled ? "bg-background/85 backdrop-blur-xl border-b border-border" : "bg-transparent"
        }`}
      >
        <div className="flex items-center justify-between px-5 lg:px-12 h-[72px]">
          <a href="#home" data-testid="nav-logo" aria-label="Faded Barber Co. — home">
            <img src="/images/logo-white.png" alt="Faded Barber Co." className="h-10 w-auto" />
          </a>
          <nav aria-label="Primary" className="hidden lg:flex items-center gap-8">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="font-mono text-xs tracking-[0.2em] uppercase text-foreground/70 hover:text-primary transition-colors duration-300"
              >
                {l.label}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <a
              href={PHONE_TEL}
              data-testid="header-call-button"
              className="hidden md:inline-flex items-center gap-2 font-mono text-xs tracking-[0.15em] text-foreground/80 hover:text-primary transition-colors duration-300"
            >
              <Phone size={14} aria-hidden="true" /> {PHONE_DISPLAY}
            </a>
            <BookLink testid="header-book-button" className="btn-primary !px-6 !py-3 hidden sm:inline-flex">
              Book Now
            </BookLink>
            <button
              data-testid="mobile-menu-toggle"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              onClick={() => setOpen(!open)}
              className="lg:hidden p-2 text-foreground"
            >
              {open ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            aria-label="Mobile"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-background/97 backdrop-blur-xl flex flex-col justify-center px-8 lg:hidden"
          >
            <ul className="space-y-2">
              {LINKS.map((l, i) => (
                <motion.li
                  key={l.href}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <a
                    href={l.href}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                    onClick={() => setOpen(false)}
                    className="font-display text-5xl uppercase tracking-wide text-foreground hover:text-primary transition-colors duration-300"
                  >
                    {l.label}
                  </a>
                </motion.li>
              ))}
            </ul>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.5 }}
              className="mt-10 flex flex-col gap-3"
            >
              <BookLink testid="mobile-menu-book-button" className="btn-primary w-full" >
                Book an Appointment
              </BookLink>
              <a href={PHONE_TEL} data-testid="mobile-menu-call-button" className="btn-outline w-full">
                <Phone size={16} aria-hidden="true" /> Call {PHONE_DISPLAY}
              </a>
            </motion.div>
          </motion.nav>
        )}
      </AnimatePresence>
    </>
  );
}
